import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useScrolled } from "../../lib/hooks";
import { cn } from "../../lib/utils";
import Magnetic from "../ui/Magnetic";

const links = [
	{ to: "/", label: "Index", no: "01" },
	{ to: "/work", label: "Work", no: "02" },
	{ to: "/about", label: "About", no: "03" },
];

export default function Nav() {
	const scrolled = useScrolled(40);
	const [open, setOpen] = useState(false);
	const location = useLocation();

	useEffect(() => setOpen(false), [location.pathname]);

	useEffect(() => {
		document.body.style.overflow = open ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	return (
		<>
			<header
				className={cn(
					"fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-out",
					scrolled ? "border-b border-edge bg-surface-page/70 backdrop-blur-xl" : "border-b border-transparent"
				)}
			>
				<div className="container flex h-[70px] items-center justify-between">
					<Link to="/" className="group flex items-center gap-3">
						<span className="relative flex h-8 w-8 items-center justify-center rounded-md border border-edge-strong bg-surface-base font-mono text-[13px] font-medium text-accent transition-colors group-hover:border-accent">
							R
							<span className="absolute -right-px -top-px h-1.5 w-1.5 rounded-full bg-accent" />
						</span>
						<span className="hidden font-display text-sm font-medium tracking-tight sm:block">
							RosieOh
							<span className="ml-2 font-mono text-[11px] font-normal text-content-muted">오태훈</span>
						</span>
					</Link>

					{/* 데스크톱 내비 */}
					<nav className="hidden items-center gap-1 md:flex">
						{links.map((link) => (
							<NavLink key={link.to} to={link.to} end={link.to === "/"}>
								{({ isActive }) => (
									<Magnetic strength={0.2}>
										<span
											className={cn(
												"relative flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[12px] uppercase tracking-[0.16em] transition-colors",
												isActive ? "text-accent" : "text-content-muted hover:text-content-primary"
											)}
										>
											<span className="text-[9px] opacity-50">{link.no}</span>
											{link.label}
											{isActive && (
												<motion.span
													layoutId="nav-pill"
													className="absolute inset-0 -z-10 rounded-full border border-accent/30 bg-accent/[0.07]"
													transition={{ type: "spring", stiffness: 380, damping: 32 }}
												/>
											)}
										</span>
									</Magnetic>
								)}
							</NavLink>
						))}
					</nav>

					<div className="flex items-center gap-3">
						<a
							href="mailto:dhxogns920@gmail.com"
							className="hidden items-center gap-2 rounded-full border border-edge-strong px-4 py-2 font-mono text-[12px] uppercase tracking-[0.16em] text-content-secondary transition-all hover:border-accent hover:text-accent md:inline-flex"
						>
							<span className="h-1.5 w-1.5 animate-blink rounded-full bg-accent" />
							Contact
						</a>

						{/* 모바일 토글 */}
						<button
							type="button"
							onClick={() => setOpen((v) => !v)}
							aria-label={open ? "메뉴 닫기" : "메뉴 열기"}
							aria-expanded={open}
							className="flex h-10 w-10 flex-col items-center justify-center gap-[5px] rounded-full border border-edge-strong md:hidden"
						>
							<span className={cn("h-px w-4 bg-content-primary transition-transform duration-300", open && "translate-y-[3px] rotate-45")} />
							<span className={cn("h-px w-4 bg-content-primary transition-transform duration-300", open && "-translate-y-[3px] -rotate-45")} />
						</button>
					</div>
				</div>
			</header>

			{/* 모바일 전체화면 메뉴 */}
			<AnimatePresence>
				{open && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						className="fixed inset-0 z-40 flex flex-col justify-center bg-surface-page/97 backdrop-blur-2xl md:hidden"
					>
						<nav className="container flex flex-col gap-2">
							{links.map((link, i) => (
								<motion.div
									key={link.to}
									initial={{ y: 40, opacity: 0 }}
									animate={{ y: 0, opacity: 1 }}
									transition={{ delay: 0.06 * i + 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
								>
									<NavLink
										to={link.to}
										end={link.to === "/"}
										className={({ isActive }) =>
											cn(
												"flex items-baseline gap-4 border-b border-edge py-5 font-display text-4xl font-semibold tracking-tight",
												isActive ? "text-accent" : "text-content-primary"
											)
										}
									>
										<span className="font-mono text-xs text-content-muted">{link.no}</span>
										{link.label}
									</NavLink>
								</motion.div>
							))}
							<motion.a
								href="mailto:dhxogns920@gmail.com"
								initial={{ y: 40, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
								className="mt-8 font-mono text-sm uppercase tracking-[0.2em] text-content-muted"
							>
								dhxogns920@gmail.com
							</motion.a>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
}
